import { generateAnalysis } from './ai-analysis'
import type { ActiveMedication, HealthEntry, LabResult } from '@/types/health'

export interface VoiceInsight {
  summary: string
  pain_level: number | null
  sleep_quality: number | null
  energy_level: number | null
  mood_level: number | null
  symptoms: string[]
  medications_taken: string[]
  urgent_alert: boolean
  watch_items: string[]
}

export interface DashboardInsight {
  current_state: string
  trend_note: string
  watch_items: string[]
  next_steps: string[]
}

function stripCodeFences(value: string): string {
  return value
    .replace(/^```(?:json)?\s*/i, '')
    .replace(/\s*```$/i, '')
    .trim()
}

function tryParseJsonObject<T>(value: string): T | null {
  const cleaned = stripCodeFences(value)

  try {
    return JSON.parse(cleaned) as T
  } catch {
    const start = cleaned.indexOf('{')
    const end = cleaned.lastIndexOf('}')

    if (start === -1 || end === -1 || end <= start) {
      return null
    }

    try {
      return JSON.parse(cleaned.slice(start, end + 1)) as T
    } catch {
      return null
    }
  }
}

function uniqueItems(items: Array<string | null | undefined>): string[] {
  return [...new Set(items.map((item) => item?.trim()).filter(Boolean) as string[])]
}

function extractMetric(transcript: string, labels: string[]): number | null {
  for (const label of labels) {
    const directMatch = transcript.match(
      new RegExp(`${label}[^\\d]{0,20}(\\d{1,2})(?:\\s*/\\s*10)?`, 'i')
    )

    if (directMatch) {
      const value = Number.parseInt(directMatch[1], 10)
      if (Number.isFinite(value) && value >= 0 && value <= 10) {
        return value
      }
    }
  }

  return null
}

function buildFallbackVoiceInsight(transcript: string): VoiceInsight {
  const normalized = transcript.toLowerCase()
  const symptomMap: Array<[RegExp, string]> = [
    [/douleur|crampe|brulure/, 'Douleurs'],
    [/fatigue|epuise|epuisement/, 'Fatigue'],
    [/ballonnement|gonfle/, 'Ballonnements'],
    [/nausee|vomissement/, 'Nausees'],
    [/diarrhee|selles/, 'Troubles digestifs'],
    [/saignement|sang/, 'Saignement'],
    [/fievre|temperature/, 'Fievre'],
    [/migraine|mal de tete/, 'Maux de tete']
  ]

  const detectedSymptoms = symptomMap
    .filter(([pattern]) => pattern.test(normalized))
    .map(([, label]) => label)

  const watchItems = uniqueItems([
    /saignement|sang/.test(normalized) ? 'Verifier les saignements signales.' : null,
    /fievre|temperature/.test(normalized) ? 'Surveiller une eventuelle fievre.' : null,
    /douleur intense|douleur forte|crise/.test(normalized)
      ? 'Douleurs importantes mentionnees dans le recit.'
      : null
  ]).slice(0, 3)

  return {
    summary: transcript.trim().slice(0, 180) || 'Recit vocal enregistre.',
    pain_level: extractMetric(normalized, ['douleur', 'crampe', 'brulure']),
    sleep_quality: extractMetric(normalized, ['sommeil', 'nuit']),
    energy_level: extractMetric(normalized, ['energie', 'fatigue']),
    mood_level: extractMetric(normalized, ['humeur', 'moral']),
    symptoms: detectedSymptoms,
    medications_taken: [],
    urgent_alert:
      /urgence|saignement important|douleur intense|douleur forte|fievre/.test(normalized),
    watch_items: watchItems
  }
}

function buildFallbackDashboardInsight(
  healthEntries: HealthEntry[],
  activeMedications: ActiveMedication[],
  labResults: LabResult[]
): DashboardInsight {
  const latestEntry = healthEntries[0] ?? null
  const abnormalResults = labResults.filter((result) => result.is_abnormal).slice(0, 3)
  const watchItems = uniqueItems([
    latestEntry?.urgent_alert ? 'Le dernier suivi quotidien contient une alerte prioritaire.' : null,
    latestEntry?.bleeding_present ? 'Un saignement est note dans la derniere entree.' : null,
    abnormalResults.length > 0
      ? `Bilans a surveiller: ${abnormalResults.map((result) => result.test_name).join(', ')}.`
      : null
  ]).slice(0, 3)

  const nextSteps = uniqueItems([
    abnormalResults.length > 0
      ? 'Montrer les derniers bilans et leur date au medecin referent.'
      : null,
    activeMedications.length > 0
      ? 'Verifier que les traitements actifs correspondent bien a la situation actuelle.'
      : 'Completer la liste des traitements si besoin.',
    latestEntry
      ? 'Continuer le journal quotidien pour garder une tendance exploitable.'
      : 'Commencer le journal quotidien pour lancer la synthese clinique.'
  ]).slice(0, 3)

  return {
    current_state:
      latestEntry?.summary ||
      (abnormalResults.length > 0
        ? 'Des bilans recents meritent une attention particuliere.'
        : 'Les donnees disponibles restent encore partielles.'),
    trend_note:
      healthEntries.length >= 3
        ? 'Une tendance commence a emerger sur les derniers suivis.'
        : 'Pas assez de recul pour une tendance fiable pour le moment.',
    watch_items: watchItems,
    next_steps: nextSteps
  }
}

function compactHealthEntries(entries: HealthEntry[]) {
  return entries.slice(0, 7).map((entry) => ({
    date: entry.entry_date,
    summary: entry.summary,
    pain_level: entry.pain_level,
    sleep_quality: entry.sleep_quality,
    energy_level: entry.energy_level,
    mood_level: entry.mood_level,
    bleeding_present: entry.bleeding_present,
    urgent_alert: entry.urgent_alert,
    symptoms: entry.symptoms
  }))
}

function compactMedications(medications: ActiveMedication[]) {
  return medications.slice(0, 8).map((medication) => ({
    name: medication.name,
    dosage: medication.dosage,
    frequency: medication.frequency,
    status: medication.current_status,
    prescriber_name: medication.prescriber_name
  }))
}

function compactLabs(labResults: LabResult[]) {
  return labResults.slice(0, 10).map((result) => ({
    test_name: result.test_name,
    test_date: result.test_date,
    value: result.result_value,
    unit: result.unit,
    is_abnormal: result.is_abnormal
  }))
}

export async function analyzeVoiceTranscript(
  patientId: string,
  transcript: string
): Promise<VoiceInsight> {
  const trimmedTranscript = transcript.trim()

  if (!trimmedTranscript) {
    return buildFallbackVoiceInsight('')
  }

  const prompt = [
    'Analyse la note vocale suivante pour un suivi de sante chronique.',
    'Reponds uniquement avec un JSON valide au format suivant:',
    '{',
    '  "summary": "resume clinique tres court",',
    '  "pain_level": number|null,',
    '  "sleep_quality": number|null,',
    '  "energy_level": number|null,',
    '  "mood_level": number|null,',
    '  "symptoms": string[],',
    '  "medications_taken": string[],',
    '  "urgent_alert": boolean,',
    '  "watch_items": string[]',
    '}',
    'Contraintes:',
    '- notes de 0 a 10 seulement si une estimation raisonnable est possible',
    '- `summary` en une phrase tres courte',
    '- `symptoms` = faits cliniques mentionnes dans le texte',
    '- `medications_taken` = traitements ou molecules cites',
    '- `watch_items` = max 3 points tres courts',
    '- aucun texte hors JSON',
    '',
    `Texte a analyser: """${trimmedTranscript}"""`
  ].join('\n')

  try {
    const response = await generateAnalysis(patientId, 'voice_analysis', prompt)
    const parsed = tryParseJsonObject<Partial<VoiceInsight>>(response.content)

    if (!parsed) {
      return buildFallbackVoiceInsight(trimmedTranscript)
    }

    const fallback = buildFallbackVoiceInsight(trimmedTranscript)

    return {
      summary: parsed.summary?.trim() || fallback.summary,
      pain_level:
        typeof parsed.pain_level === 'number' ? parsed.pain_level : fallback.pain_level,
      sleep_quality:
        typeof parsed.sleep_quality === 'number'
          ? parsed.sleep_quality
          : fallback.sleep_quality,
      energy_level:
        typeof parsed.energy_level === 'number'
          ? parsed.energy_level
          : fallback.energy_level,
      mood_level:
        typeof parsed.mood_level === 'number' ? parsed.mood_level : fallback.mood_level,
      symptoms: uniqueItems([...(parsed.symptoms || []), ...fallback.symptoms]),
      medications_taken: uniqueItems(parsed.medications_taken || []),
      urgent_alert:
        typeof parsed.urgent_alert === 'boolean'
          ? parsed.urgent_alert
          : fallback.urgent_alert,
      watch_items: uniqueItems([...(parsed.watch_items || []), ...fallback.watch_items]).slice(0, 3)
    }
  } catch {
    return buildFallbackVoiceInsight(trimmedTranscript)
  }
}

export async function generateDashboardInsight(
  patientId: string,
  healthEntries: HealthEntry[],
  activeMedications: ActiveMedication[],
  labResults: LabResult[]
): Promise<DashboardInsight> {
  if (
    healthEntries.length === 0 &&
    activeMedications.length === 0 &&
    labResults.length === 0
  ) {
    return {
      current_state: 'Ajoutez quelques donnees pour lancer la synthese clinique.',
      trend_note: 'Le tableau de bord commencera a relier les donnees des que le suivi sera plus fourni.',
      watch_items: [],
      next_steps: ['Completer le journal du jour ou importer un bilan recent.']
    }
  }

  const prompt = [
    'Tu prepares une synthese clinique ultra courte pour un tableau de bord patient.',
    'A partir des donnees ci-dessous, reponds uniquement avec un JSON valide:',
    '{',
    '  "current_state": "1 phrase",',
    '  "trend_note": "1 phrase",',
    '  "watch_items": ["max 3 points"],',
    '  "next_steps": ["max 3 actions concretes"]',
    '}',
    'Regles:',
    '- style tres concis, utile pour la patiente et son medecin',
    '- pas de diagnostic',
    '- pas de texte hors JSON',
    '',
    `Journal recent: ${JSON.stringify(compactHealthEntries(healthEntries))}`,
    `Traitements actifs: ${JSON.stringify(compactMedications(activeMedications))}`,
    `Bilans recents: ${JSON.stringify(compactLabs(labResults))}`
  ].join('\n')

  try {
    const response = await generateAnalysis(patientId, 'comprehensive_health', prompt)
    const parsed = tryParseJsonObject<Partial<DashboardInsight>>(response.content)

    if (!parsed) {
      return buildFallbackDashboardInsight(healthEntries, activeMedications, labResults)
    }

    const fallback = buildFallbackDashboardInsight(
      healthEntries,
      activeMedications,
      labResults
    )

    return {
      current_state: parsed.current_state?.trim() || fallback.current_state,
      trend_note: parsed.trend_note?.trim() || fallback.trend_note,
      watch_items: uniqueItems(parsed.watch_items || fallback.watch_items).slice(0, 3),
      next_steps: uniqueItems(parsed.next_steps || fallback.next_steps).slice(0, 3)
    }
  } catch {
    return buildFallbackDashboardInsight(healthEntries, activeMedications, labResults)
  }
}
